import type { KarrotLocalCountryCode } from "@karrotmarket-com/core";
import {
  type BrowserHttpClient,
  clientBuilder,
} from "@karrotmarket-com/http-client-builder/browser";
import { isNil } from "lodash";
import type { Location, RegionFilterOptions } from "services";
import type { components } from "src-server/__generated__/hoian-webapp/web";

const client = clientBuilder.withoutBaseURL().invoke();

const createServices = () => ({
  karrotmarket: {
    searchLocation: async ({
      karrotLocalCountryCode,
      keyword,
      coords,
    }: {
      karrotLocalCountryCode: KarrotLocalCountryCode;
      keyword?: string;
      coords?: {
        lat: number;
        lng: number;
      };
    }): Promise<{ locations: Location[] }> => {
      return client.get({
        url: `/v1/api/search/${karrotLocalCountryCode}/location`,
        queryObject: {
          keyword,
          ...(coords ?? {}),
        },
      });
    },
    getRegionFilterOptions: async ({
      regionId,
      karrotLocalCountryCode,
    }: {
      regionId: string;
      karrotLocalCountryCode: KarrotLocalCountryCode;
    }): Promise<{ regionFilterOptions: RegionFilterOptions }> => {
      return client.get({
        url: `/v1/api/filter/${karrotLocalCountryCode}/region/${regionId}`,
      });
    },
    findAllRegion: async (
      keyword: string,
    ): Promise<{
      allRegions: {
        id: number;
        depth: number;
        name: string;
        name1: string;
        name2: string;
        name3: string;
        countryName: KarrotLocalCountryCode;
      }[];
    }> => {
      return client.get({ url: `/v1/api/search/all-locations/${keyword}` });
    },
    uploadImageToBigpicture: async ({
      countryCode,
      files,
    }: {
      countryCode: KarrotLocalCountryCode;
      files: File[];
    }): Promise<{
      images: {
        id: string;
      }[];
    }> => {
      const imageIdList = await Promise.all(
        files.map((file) => uploadImage({ countryCode, file })),
      );

      return {
        images: imageIdList.map((image) => ({
          id: image.imageId,
        })),
      };
    },
    setLocation: async ({
      karrotLocalCountryCode,
      location,
    }: {
      karrotLocalCountryCode: KarrotLocalCountryCode;
      location: {
        h3Index: number;
        locationName: string;
        regionId: number;
        regionName: string;
        countryCode: string;
      };
    }) => {
      return (client as BrowserHttpClient).post({
        url: `/v1/api/fleamarket/${karrotLocalCountryCode}/session/set-location`,
        init: {
          body: JSON.stringify(location),
          headers: {
            "Content-Type": "application/json",
          },
        },
      });
    },
    postArticle: async ({
      countryCode,
      data,
    }: {
      countryCode: KarrotLocalCountryCode;
      data: components["schemas"]["Articles.ArticleCreateRequest"];
    }) => {
      return (client as BrowserHttpClient).post<
        components["schemas"]["Articles.Article"]
      >({
        url: `/v1/api/fleamarket/${countryCode}/article/new`,
        init: {
          body: JSON.stringify(data),
          headers: {
            "Content-Type": "application/json",
          },
        },
      });
    },
    getInterestedUsers: ({
      articleId,
      karrotLocalCountryCode,
    }: {
      articleId: string;
      karrotLocalCountryCode: KarrotLocalCountryCode;
    }): Promise<
      {
        userId: number;
        nickname: string;
        profileImageUrl: string;
        locationName: string;
        lastMessageAt: string;
      }[]
    > => {
      return client.get({
        url: `/v1/api/fleamarket/${karrotLocalCountryCode}/articles/${articleId}/interested_users`,
      });
    },

    changeArticleStatusToReserved: ({
      articleId,
      reservedUserId,
      karrotLocalCountryCode,
    }: {
      articleId: string;
      reservedUserId: number;
      karrotLocalCountryCode: KarrotLocalCountryCode;
    }): Promise<void> => {
      return client.put({
        url: `/v1/api/fleamarket/${karrotLocalCountryCode}/articles/${articleId}/statuses/reserved`,
        init: {
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reservedUserId }),
        },
      });
    },

    changeArticleStatusToOngoing: ({
      articleId,
      karrotLocalCountryCode,
    }: {
      articleId: string;
      karrotLocalCountryCode: KarrotLocalCountryCode;
    }): Promise<void> => {
      return client.put({
        url: `/v1/api/fleamarket/${karrotLocalCountryCode}/articles/${articleId}/statuses/ongoing`,
      });
    },

    changeArticleStatusToSold: ({
      articleId,
      buyerUserId,
      karrotLocalCountryCode,
    }: {
      articleId: string;
      buyerUserId?: number;
      karrotLocalCountryCode: KarrotLocalCountryCode;
    }): Promise<void> => {
      return client.put({
        url: `/v1/api/fleamarket/${karrotLocalCountryCode}/articles/${articleId}/statuses/sold`,
        ...(isNil(buyerUserId)
          ? {}
          : {
              init: {
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ buyerUserId: buyerUserId }),
              },
            }),
      });
    },

    getCategoryPredictions: async ({
      countryCode,
      title,
      limit,
    }: {
      countryCode: KarrotLocalCountryCode;
      title: string;
      limit?: number;
    }) => {
      return client.get<
        components["schemas"]["Categories.CategoryPrediction"][]
      >({
        url: `/v1/api/fleamarket/${countryCode}/categories/predictions`,
        queryObject: {
          title,
          limit,
        },
      });
    },

    getCreateArticlePermission: ({
      karrotLocalCountryCode,
    }: {
      karrotLocalCountryCode: KarrotLocalCountryCode;
    }) => {
      return client.get({
        url: `/v1/api/fleamarket/${karrotLocalCountryCode}/articles/permissions/create`,
      });
    },

    getEditArticlePermission: ({
      articleId,
      karrotLocalCountryCode,
    }: {
      articleId: string;
      karrotLocalCountryCode: KarrotLocalCountryCode;
    }) => {
      return client.get({
        url: `/v1/api/fleamarket/${karrotLocalCountryCode}/articles/${articleId}/permissions/edit`,
      });
    },

    getStartChatPermission: ({
      articleId,
      karrotLocalCountryCode,
    }: {
      articleId: string;
      karrotLocalCountryCode: KarrotLocalCountryCode;
    }) => {
      return client.get({
        url: `/v1/api/fleamarket/${karrotLocalCountryCode}/articles/${articleId}/permissions/start-chat`,
      });
    },
  },
});

export const services = (() => {
  let cachedServices: ReturnType<typeof createServices> | null = null;

  return {
    getInstance: () => {
      if (cachedServices) {
        return cachedServices;
      }

      cachedServices = createServices();
      return cachedServices;
    },
  };
})();

type UploadImageParams = {
  countryCode: KarrotLocalCountryCode;
  file: File;
};
async function uploadImage({ countryCode, file }: UploadImageParams) {
  const response = await client.post<{
    result: {
      presignedUrl: { id: string; upload_url: string };
    };
  }>({
    url: `/v1/api/bigpicture/${countryCode}/presigned-url/chat`,
    init: {
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        uploadTarget: {
          filename: file.name,
          mimetype: file.type,
        },
      }),
    },
  });

  await client.put({
    url: response.result.presignedUrl.upload_url,
    init: {
      headers: {
        "x-amz-meta-id": response.result.presignedUrl.id,
        "Content-Type": file.type,
      },
      body: await file.arrayBuffer(),
    },
  });

  return {
    imageId: response.result.presignedUrl.id,
  };
}
